import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatApiError } from "@/lib/utils";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { VariantChooser, ReadyDot, Price, Money, Photo, wt, ct } from "./VariantChooser";
import { COLOR_NAME } from "./mcApi";

const Row = ({ label, fr, cu, testid, strong }) => (
  <tr className={`border-t border-[#0D1B2A]/[0.06] ${strong ? "font-semibold text-[#0D1B2A]" : "text-slate-700"}`} data-testid={testid}>
    <td className="py-2 pr-3 text-[13px]">{label}</td>
    <td className="py-2 pr-3 text-right text-[13px]">{fr}</td>
    <td className="py-2 text-right text-[13px]">{cu}</td>
  </tr>
);

const Breakup = ({ p }) => {
  const b = p.breakup;
  if (!b) return null;
  return (
    <table className="w-full" data-testid="mc-breakup-table">
      <thead><tr className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
        <th className="text-left py-1.5 font-normal">Cost &amp; Sell</th><th className="text-right font-normal">Franchise</th><th className="text-right font-normal">Customer MRP</th>
      </tr></thead>
      <tbody>
        <Row label={<>Metal <span className="text-slate-400">(<Price v={b.metal_rate_per_g} />/g)</span></>} fr={<Price v={b.metal} />} cu={<Price v={b.metal} />} testid="mc-row-metal" />
        <Row label="Labour" fr={<Price v={b.franchise.labour} />} cu={<Price v={b.customer.labour} />} testid="mc-row-labour" />
        <Row label="Diamond" fr={<Price v={b.franchise.diamond} />} cu={<Price v={b.customer.diamond} />} testid="mc-row-diamond" />
        <Row label="Sub-total" fr={<Price v={b.franchise.subtotal} />} cu={<Price v={b.customer.subtotal} />} testid="mc-row-subtotal" />
        <Row label={`GST (${(b.gst_rate * 100).toFixed(0)}%)`} fr="—" cu={<Price v={b.customer.gst} />} testid="mc-row-gst" />
        <Row label="Total" fr={<Price v={b.franchise.total} />} cu={<Price v={b.customer.total} />} testid="mc-row-total" strong />
        <Row label="GP vs Customer MRP" fr={<><Price v={b.franchise_gp} /> <span className="mc-num text-slate-400">({b.franchise_gp_pct}%)</span></>} cu="" testid="mc-row-gp" />
        <Row label="Avg diamond rate / ct" fr={<Price v={b.franchise.avg_dia_rate} />} cu={<Price v={b.customer.avg_dia_rate} />} testid="mc-row-avg-dia" />
      </tbody>
      <tfoot><tr><td colSpan={3} className="pt-2 text-[11px] text-slate-400">{b.rounding_note} (raw <Price v={b.customer.total_raw} />)</td></tr></tfoot>
    </table>
  );
};

// CS 22.13 -- distributor-typed links: DISTRIBUTION COST / RETAILER / CUSTOMER MRP.
const Row4 = ({ label, d, r, cu, testid, strong }) => (
  <tr className={`border-t border-[#0D1B2A]/[0.06] ${strong ? "font-semibold text-[#0D1B2A]" : "text-slate-700"}`} data-testid={testid}>
    <td className="py-2 pr-3 text-[13px]">{label}</td>
    <td className="py-2 pr-3 text-right text-[13px]">{d}</td>
    <td className="py-2 pr-3 text-right text-[13px]">{r}</td>
    <td className="py-2 text-right text-[13px]">{cu}</td>
  </tr>
);
const Gp = ({ g }) => <><Price v={g.amount} /> <span className="mc-num text-slate-400">({g.pct}%)</span></>;

const DistBreakup = ({ p }) => {
  const b = p.breakup;
  if (!b) return null;
  const tiers = [b.franchise, b.retailer, b.customer];
  const row = (label, k, testid, strong) => (
    <Row4 label={label} d={<Price v={tiers[0][k]} />} r={<Price v={tiers[1][k]} />} cu={<Price v={tiers[2][k]} />} testid={testid} strong={strong} />
  );
  return (
    <div className="w-full min-w-0 overflow-x-auto">
    <table className="w-full min-w-[460px]" data-testid="mc-breakup-table">
      <thead><tr className="text-[10px] uppercase tracking-[0.14em] text-slate-400">
        <th className="text-left py-1.5 font-normal">Cost &amp; Sell</th><th className="text-right font-normal">Distribution cost</th><th className="text-right font-normal">Retailer</th><th className="text-right font-normal">Customer MRP</th>
      </tr></thead>
      <tbody>
        <Row4 label={<>Metal <span className="text-slate-400">(<Price v={b.metal_rate_per_g} />/g)</span></>} d={<Price v={b.metal} />} r={<Price v={b.metal} />} cu={<Price v={b.metal} />} testid="mc-row-metal" />
        {row("Labour", "labour", "mc-row-labour")}
        {row("Diamond", "diamond", "mc-row-diamond")}
        {row("Sub-total", "subtotal", "mc-row-subtotal")}
        <Row4 label={`GST (${(b.gst_rate * 100).toFixed(0)}%)`} d="—" r="—" cu={<Price v={b.customer.gst} />} testid="mc-row-gst" />
        {row("Total", "total", "mc-row-total", true)}
        <Row4 label="GP vs Retailer Price" d={<Gp g={b.gp_vs_retailer} />} r="" cu="" testid="mc-row-gp-retailer" />
        <Row4 label="GP vs Customer MRP" d={<Gp g={b.gp_vs_mrp} />} r="" cu="" testid="mc-row-gp-mrp" />
        {row("Avg diamond rate / ct", "avg_dia_rate", "mc-row-avg-dia")}
      </tbody>
      <tfoot><tr><td colSpan={4} className="pt-2 text-[11px] text-slate-400">{b.rounding_note} (raw <Price v={b.customer.total_raw} />)</td></tr></tfoot>
    </table>
    </div>
  );
};

const TotalBox = ({ label, v, testid, accent }) => (
  <div className={`rounded-lg border p-4 ${accent ? "border-[#C9A84C]/40 bg-[#FAF8F3]" : "border-[#0D1B2A]/[0.07]"}`}>
    <div className={`text-[10px] uppercase tracking-[0.2em] ${accent ? "text-[#8a6d1f]" : "text-slate-400"}`}>{label}</div>
    <Price v={v} className={`text-xl sm:text-2xl text-[#0D1B2A] ${accent ? "font-medium" : ""}`} testid={testid} />
  </div>
);

const uploadedOf = (card) => ({ color: card?.uploaded_variant?.color || null, purity: card?.uploaded_variant?.purity || null });

// True only when a detail response verifiably belongs to this exact product + variant.
const identityMatches = (data, card, v) =>
  !!data && data.design_key === card?.design_key &&
  data.chosen?.color === v.color && data.chosen?.purity === v.purity;

// CS 22.2 — one authoritative variant identity. The parent mounts a FRESH instance
// per product open (key={design_key}, conditional render), so the selected variant
// initialises to the uploaded/default variant before any detail request fires.
// Every detail response is applied only if it passes the generation + identity guard.
export const DesignDetail = ({ api, card, initial, onClose, onAdd }) => {
  const up = uploadedOf(card);
  const [v, setV] = useState({ color: initial?.color || up.color, purity: initial?.purity || up.purity });
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(!!(up.color && up.purity));
  const [err, setErr] = useState(null);
  const [tick, setTick] = useState(0);
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const genRef = useRef(0);
  const colors = ["Y", "W", ...(up.color && !["Y", "W"].includes(up.color) ? [up.color] : [])];

  useEffect(() => {
    if (!card || !v.color || !v.purity) { setD(null); setLoading(false); return; }
    const gen = ++genRef.current;
    const key = card.design_key, color = v.color, purity = v.purity;
    let live = true;
    setD(null); setErr(null); setLoading(true);   // withhold any prior variant's values
    api.design({ key, color, purity })
      .then((data) => {
        if (!live || gen !== genRef.current) return;   // late / out-of-order: discard
        if (!identityMatches(data, card, { color, purity })) {
          setErr("Could not verify that the loaded details belong to the selected variant. Discarded for safety — please retry.");
          setLoading(false);
          return;
        }
        setD(data); setLoading(false);
      })
      .catch((e) => {
        if (!live || gen !== genRef.current) return;
        setErr(formatApiError(e.response?.data?.detail) || "Failed to load this variant");
        setLoading(false);
      });
    return () => { live = false; };
  }, [api, card, v.color, v.purity, tick]);

  // Render-side guard: even a stray state value can only render if it matches the
  // currently selected variant and product.
  const shown = identityMatches(d, card, v) ? d : null;
  const p = shown?.pricing;
  const isUploaded = v.color === up.color && v.purity === up.purity;
  const add = async () => {
    setBusy(true);
    try { await onAdd({ design_key: card.design_key, color: v.color, purity: v.purity, qty }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!card} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="mc-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl tracking-wide flex items-center gap-2" data-testid="mc-detail-title">
            {card?.design_key} {shown?.ready && <ReadyDot testid="mc-detail-ready" />}
          </DialogTitle>
          <DialogDescription className="text-[12px] tracking-wide">{card?.category}{card?.is_al ? " · Ashth Lite" : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-[240px_1fr] gap-6">
          <div className="aspect-square rounded-lg border border-[#0D1B2A]/[0.07] p-3" data-testid="mc-detail-photo">
            <Photo url={shown?.photo_url || card?.photo_url} size={shown?.photo_size || card?.photo_size} alt={card?.design_key || ""} />
          </div>
          <div className="space-y-4">
            <div className="rounded-lg bg-[#FAF8F3] px-3 py-2 text-[12px] flex items-center justify-between gap-2" data-testid="mc-detail-uploaded-variant">
              <span><span className="text-[10px] uppercase tracking-[0.18em] text-[#8a6d1f]">Uploaded variant</span>{" "}
                {COLOR_NAME[up.color] || "—"} {up.purity} <span className="text-slate-400">({card?.uploaded_style_no})</span></span>
              {!isUploaded && (
                <button onClick={() => setV(up)} className="flex items-center gap-1 text-[11px] text-[#0D1B2A] hover:text-[#8a6d1f]" data-testid="mc-detail-reset-uploaded">
                  <RotateCcw className="w-3 h-3" /> Back to uploaded
                </button>
              )}
            </div>
            <VariantChooser color={v.color} purity={v.purity} onChange={setV} uploaded={up} colors={colors} testid="mc-detail-variant" />
            {loading && (
              <div className="space-y-2" data-testid="mc-detail-loading">
                <div className="skeleton h-4 w-3/4" /><div className="skeleton h-4 w-1/2" /><div className="skeleton h-4 w-2/3" />
              </div>
            )}
            {err && !loading && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 flex items-center justify-between gap-3" data-testid="mc-detail-error">
                <span>{err}</span>
                <button onClick={() => setTick((t) => t + 1)} className="text-xs underline whitespace-nowrap" data-testid="mc-detail-retry">Retry</button>
              </div>
            )}
            {shown && !loading && (
              <div className="text-[13px] space-y-1.5 text-slate-700" data-testid="mc-detail-variant-info">
                <div data-testid="mc-detail-showing"><span className="text-slate-400">Showing</span> {COLOR_NAME[v.color]} {v.purity}{" "}
                  {shown.is_estimate
                    ? <span className="text-amber-700" data-testid="mc-detail-estimate">· variant estimate from {shown.reference.style_no} ({shown.reference.anchor_purity}, <span className="mc-num">{wt(shown.reference.net_weight)}</span>)</span>
                    : <span className="text-[#8a6d1f]" data-testid="mc-detail-physical">· {isUploaded ? "uploaded variant" : "existing piece"} ({shown.reference.style_no})</span>}
                </div>
                {p?.priceable && <div data-testid="mc-detail-weight"><span className="text-slate-400">Net gold weight</span> <span className="mc-num" data-testid="mc-detail-weight-value">{wt(p.converted_net_weight)}</span></div>}
                <div data-testid="mc-detail-diamond"><span className="text-slate-400">Diamonds</span>{" "}
                  <span className="mc-num">{shown.diamond.pieces} pcs · {ct(shown.diamond.weight_ct)}</span>
                  {shown.diamond_lines.length > 0 && <span className="text-slate-400"> ({shown.diamond_lines.map((l) => `${l.shape_code} ${l.pieces}/${Number(l.weight_ct).toFixed(2)}`).join(" · ")})</span>}
                </div>
              </div>
            )}
          </div>
        </div>
        {p && !p.priceable && p.unpriceable_reason === "diamond_not_in_price_list" && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="mc-detail-price-on-request">
            Price on request. Diamond shape/size not in this price list{p.unpriced_shapes?.length ? `: ${p.unpriced_shapes.join(", ")}` : ""}.
          </div>
        )}
        {p && !p.priceable && p.unpriceable_reason !== "diamond_not_in_price_list" && (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="mc-detail-unpriceable">
            No valid estimate for this variant ({p.unpriceable_reason}{p.unpriced_shapes ? `: ${p.unpriced_shapes.join(", ")}` : ""}). Flagged for data review.
          </div>
        )}
        {loading && (
          <div className="grid grid-cols-2 gap-3" data-testid="mc-detail-totals-loading">
            <div className="skeleton h-20" /><div className="skeleton h-20" />
          </div>
        )}
        {p?.priceable && !loading && "wholesaler_cost" in p && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0" data-testid="mc-detail-totals">
            <div className="rounded-lg border border-[#0D1B2A]/[0.07] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Wholesaler cost</div>
              <Money v={p.wholesaler_cost} cur={p.cost_currency} className="text-xl sm:text-2xl text-[#0D1B2A]" testid="mc-detail-wcost" /></div>
            <div className="rounded-lg border border-[#C9A84C]/40 bg-[#FAF8F3] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6d1f]">B2B price</div>
              {p.b2b_price == null ? <div className="text-sm text-slate-500 mt-1" data-testid="mc-detail-b2b">Not set (selling rates not locked)</div>
                : <Money v={p.b2b_price} cur={p.selling_currency} className="text-xl sm:text-2xl text-[#0D1B2A] font-medium" testid="mc-detail-b2b" />}</div>
          </div>
        )}
        {p?.priceable && !loading && "retailer_total" in p && (
          <div className="space-y-4 min-w-0">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="mc-detail-totals">
              <TotalBox label="Distribution cost" v={p.franchise_total} testid="mc-detail-dist" />
              <TotalBox label="Retailer price" v={p.retailer_total} testid="mc-detail-retailer" />
              <TotalBox label="Customer MRP" v={p.customer_total} testid="mc-detail-mrp" accent />
            </div>
            {p.is_al ? <p className="text-xs text-slate-500" data-testid="mc-detail-al-note">Ashth Lite — final values only.</p> : <DistBreakup p={p} />}
            <p className="text-[11px] text-slate-400" data-testid="mc-detail-versions">Live estimate · rate v{p.rate_version} · weight table v{p.weight_table_version}</p>
          </div>
        )}
        {p?.priceable && !loading && !("retailer_total" in p) && !("wholesaler_cost" in p) && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3" data-testid="mc-detail-totals">
              <div className="rounded-lg border border-[#0D1B2A]/[0.07] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Franchise cost</div>
                <Price v={p.franchise_total} className="text-2xl text-[#0D1B2A]" testid="mc-detail-franchise" /></div>
              <div className="rounded-lg border border-[#C9A84C]/40 bg-[#FAF8F3] p-4"><div className="text-[10px] uppercase tracking-[0.2em] text-[#8a6d1f]">Suggested MRP</div>
                <Price v={p.customer_total} className="text-2xl text-[#0D1B2A] font-medium" testid="mc-detail-mrp" /></div>
            </div>
            {p.is_al ? <p className="text-xs text-slate-500" data-testid="mc-detail-al-note">Ashth Lite — final values only.</p> : <Breakup p={p} />}
            <p className="text-[11px] text-slate-400" data-testid="mc-detail-versions">Live estimate · rate v{p.rate_version} · weight table v{p.weight_table_version}</p>
          </div>
        )}
        {onAdd && (
          <div className="flex items-center gap-3 pt-3 border-t border-[#0D1B2A]/10">
            <div className="flex items-center rounded-full border border-[#0D1B2A]/15">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-2 hover:text-[#8a6d1f]" data-testid="mc-detail-qty-minus"><Minus className="w-3.5 h-3.5" /></button>
              <span className="mc-num w-8 text-center text-sm" data-testid="mc-detail-qty">{qty}</span>
              <button onClick={() => setQty(Math.min(999, qty + 1))} className="p-2 hover:text-[#8a6d1f]" data-testid="mc-detail-qty-plus"><Plus className="w-3.5 h-3.5" /></button>
            </div>
            <Button onClick={add} disabled={!p?.priceable || busy || loading} className="bg-[#0D1B2A] text-white rounded-full px-6" data-testid="mc-detail-add-btn">
              {busy ? "Saving…" : "Add to selection"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
